// Hàm THUẦN (0 I/O) cho merge/strip Avatar block khỏi nội dung dạng
// .gitignore. Tách từ write-or-merge-gitignore.ts + execute-uninstall-deletion.ts
// (ticket #138 phase-01) để dùng chung cho `info/exclude` (phase 02-04) — DRY,
// tránh nhân bản logic marker lần 3/4.
import { AVATAR_MARKER_END, AVATAR_MARKER_START } from "./gitignore-template-loader.js";

// Merge avatarBlock vào existing content. Hành vi giống hệt
// write-or-merge-gitignore.ts:17-38 (bản cũ), TRỪ 1 ca đổi có chủ đích (N1b):
// existing rỗng/chỉ whitespace + không marker → trả `avatarBlock` verbatim,
// KHÔNG phải `"\n\n" + avatarBlock` như trước.
export function mergeAvatarBlockIntoContent(existing: string, avatarBlock: string): string {
  const startIdx = existing.indexOf(AVATAR_MARKER_START);
  const endIdx = existing.indexOf(AVATAR_MARKER_END);

  // Đã có marker hợp lệ (END sau START) → replace block giữa marker, giữ
  // content trước/sau.
  if (startIdx !== -1 && endIdx !== -1 && endIdx > startIdx) {
    const before = existing.slice(0, startIdx);
    const after = existing.slice(endIdx + AVATAR_MARKER_END.length);
    return `${before.trimEnd()}\n\n${avatarBlock}${after.trimStart()}`;
  }

  // START có nhưng END mất/hỏng (endIdx=-1 hoặc đứng trước START) → file bị sửa
  // tay hỏng. Bỏ phần Avatar hỏng từ startIdx trở đi rồi ghi block sạch — tránh
  // nhánh append tạo header thứ 2 (orphan). Giữ idempotent (1 marker).
  if (startIdx !== -1) {
    const before = existing.slice(0, startIdx);
    return `${before.trimEnd()}\n\n${avatarBlock}`;
  }

  // Không marker, existing rỗng hoặc chỉ whitespace (N1b — đổi hành vi CÓ CHỦ
  // ĐÍCH so với bản cũ): trả block verbatim, không thêm 2 newline dẫn vô nghĩa.
  if (existing.trim().length === 0) {
    return avatarBlock;
  }

  // Không marker, có content → append phía cuối, thêm newline phân cách.
  return `${existing.trimEnd()}\n\n${avatarBlock}`;
}

export type StripAvatarBlockStatus = "stripped" | "no-marker" | "corrupted";

export interface StripAvatarBlockResult {
  content: string | null;
  status: StripAvatarBlockStatus;
}

// Strip avatarBlock khỏi existing content. Công thức nhánh "stripped" là NGUYÊN VĂN hàm strip cũ
// của execute-uninstall-deletion (bản TRƯỚC phase-01, phase-01 bê về đây; đừng trích lại theo số
// dòng bên file đó — dải cũ nay là code khác). Dòng chốt = dòng gán `content` thêm `\n` đuôi; dừng
// ở `.trim()` là thiếu ĐÚNG 1 byte ⇒ khác `HEAD:.gitignore` ⇒ repo VẪN `M`. Xem phase-01 §KI-2.
export function stripAvatarBlockFromContent(existing: string): StripAvatarBlockResult {
  const startIdx = existing.indexOf(AVATAR_MARKER_START);
  const endIdx = existing.indexOf(AVATAR_MARKER_END);

  if (startIdx === -1) {
    return { content: null, status: "no-marker" };
  }

  // END mất HOẶC đứng trước START (N1 — ca đổi hành vi CÓ CHỦ ĐÍCH so với code
  // cũ chỉ chặn `endIdx === -1`) → file bị hỏng tay, KHÔNG đụng vào file.
  if (endIdx === -1 || endIdx < startIdx) {
    return { content: null, status: "corrupted" };
  }

  const before = existing.slice(0, startIdx);
  const after = existing.slice(endIdx + AVATAR_MARKER_END.length);
  const cleaned = `${before.trimEnd()}\n${after.trimStart()}`.trim();
  const content = cleaned === "" ? "" : `${cleaned}\n`;
  return { content, status: "stripped" };
}
